/** @format */
import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import React, {useCallback, useEffect, useMemo}                                      from 'react';
import { ItemLoaders }                                                               from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { configDefault } from '../../core/config';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import ViewButton from '../../components/Buttons/ViewButton';
import { history }                                                from '../../core/history';
import {LinearBillingCollectionInterface, LinearBillingInterface} from "../../interfaces/billing/LinearBillingInterface";
import {tariffConstants} from "../../modules/tariffs/actions";
import Filter from "../../interfaces/forms/Filter";
import Order from "../../interfaces/forms/Order";
import DeleteButton from "../../components/Buttons/DeleteButton";
import Pager from "../../components/Adescom/Pager";
import {NotificationHelper} from "../../helpers/NotificationHelper";
import {operationAction} from "../../modules/block/OperationStatus";
import AlertInfo from "../../components/Info/AlertInfo";
import Loader from "../../components/Loaders/Loader";

export const PrefixPricelistList = (): React.ReactElement => {
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();
    const dispatch = useDispatch();
    const { id } = useParams();

    const [alert, setAlert] = React.useState<React.ReactElement | null>(null);

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };

    const itemLoader = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);

    const defaultFilters = useMemo<Array<Filter>>(() => {
        return [
            { name: 'prefixId', value: id }
        ];
    }, [id]);

    const [filters, setFilters] = React.useState<Array<Filter>>([]);
    const [orders, setOrders] = React.useState<Array<Order>>([]);


    const [loading, setLoading] = React.useState(false);
    const [linearBillings, setLinearBillings] = React.useState<LinearBillingCollectionInterface|null>(null);

    const paginationCallbackFunction = useCallback(
        (uri: string): void => {
            if(!id){
                return;
            }

            setLoading(true);
            const tmpFilters = [...defaultFilters, ...filters];
            const tmpOrders = [...orders];


            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                tariffConstants.LIST,
                tmpFilters,
                tmpOrders,
                {
                    onSuccess: (data: any) => {
                        setLoading(false);
                        setLinearBillings(data);
                    },
                    onError: () => {
                        setLoading(false);
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [id, itemLoader, filters, defaultFilters, orders]
    );

    useEffect(() => {
        paginationCallbackFunction(FormGeneralHelper.getPath('/linear_billings', configDefault.PLATFORM_API_PREFIX));
    }, [paginationCallbackFunction]);

    const redirectToTariffEdit = useCallback((data: {tariffId: number}): void => {
        history.push({
            pathname: FormGeneralHelper.getRouteUrl(`/tariff-edit/${data.tariffId}`),
        });
    }, []);

    const confirmRemovePrefixFromPricelist = useCallback((data: {pricelistName: string, tariffName: string, id: number, prefixId: number}): void => {
        itemLoader.deleteWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(`/linear_billings/${data.id}`, configDefault.PLATFORM_API_PREFIX)),
            tariffConstants.DELETE,
            {
                onSuccess: () => {
                    paginationCallbackFunction(FormGeneralHelper.getPath('/linear_billings', configDefault.PLATFORM_API_PREFIX));
                    NotificationHelper.notifySuccess('prefixes:notify.prefix_removed_from_pricelist', dispatch);
                    setAlert(null);
                },
                onError: () => {
                    NotificationHelper.notifyError('prefixes:notify.error', dispatch)
                }
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: false
            }
        )
    }, [itemLoader, dispatch, paginationCallbackFunction]);

    const removePrefixFromPricelist = useCallback((data: {pricelistName: string, tariffName: string, id: number, prefixId: number}): void => {
        setAlert(<AlertInfo
            onConfirm={(): void => {
                confirmRemovePrefixFromPricelist(data);
            }}
            onCancel={(): void => {
                setAlert(null);
            }}
            confirmBtnText={'general:common.confirm'}
            cancelBtnText={'general:common.cancel'}
            content="prefixes:notify.prefix_remove_confirm"
            valueObject={{pricelistName: data.pricelistName, tariffName: data.tariffName}}
        />);
    }, [confirmRemovePrefixFromPricelist]);

    return (
        <>
            {alert}
            <GridContainer>
                <GridItem>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell textAlign="left" title="clients:c.commands" />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="pricelists:h.pricelist_schema_element_name"
                                        fieldName="mainname"
                                        showSort={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                    />
                                </TableCell>
                                <TableCell classes={tableStickyHeader} align="left">
                                    <TableHeaderContentCell
                                        textAlign="left"
                                        title="pricelists:h.tariff_name"
                                        fieldName="tariff.tariffName"
                                        showSort={false}
                                        defaultDirection="desc"
                                        searchFieldType="string"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {linearBillings &&
                                linearBillings['hydra:member'] &&
                                linearBillings['hydra:member'].map((el: any) => {
                                    return (
                                        <TableRow key={el.id}>
                                            <TableCell classes={tableCellRoot}>
                                                <>
                                                    {el.tariff && (
                                                        <ViewButton
                                                            callback={redirectToTariffEdit}
                                                            callbackParam={{ tariffId: el.tariff.id }}
                                                        />
                                                    )}
                                                    <DeleteButton
                                                        callback={removePrefixFromPricelist}
                                                        callbackParam={{ id: el.id, prefixId: id, pricelistName: el.mainname, tariffName: el?.tariff?.tariffName ?? '' }}
                                                    />
                                                </>
                                            </TableCell>
                                            <TableCell classes={tableCellRoot}>{el.mainname && el.mainname}</TableCell>
                                            <TableCell classes={tableCellRoot}>
                                                {el.tariff && el.tariff.tariffName}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell classes={tableCellRoot} colSpan={3}>
                                    {linearBillings && linearBillings['hydra:view'] && (
                                        <Pager callback={paginationCallbackFunction} views={linearBillings['hydra:view']} />
                                    )}
                                </TableCell>
                            </TableRow>
                            <Loader visible={loading}/>
                        </TableFooter>
                    </Table>
                </GridItem>
            </GridContainer>
        </>
    );
};
